/**
 * Which language the staff UI speaks — the pure half (fleet i18n standard; ptc-statetax #54 is the
 * reference, ruled for supervision 2026-10-21).
 *
 * 🔑 NO DEFAULT IS SET BY ANYONE. Staff choose with the header toggle, and the choice is remembered:
 *   1. the person's own choice — stored SERVER-SIDE per person (`/my-preferences`), so it follows
 *      them between computers, and cached in localStorage so the first paint is already in it (no
 *      flash of English);
 *   2. the browser's language, until they choose;
 *   3. English.
 * Nothing is read from, or written to, a staff record.
 *
 * The local cache is keyed by user (like column layout, `useColumnPrefs`), so two people sharing a
 * machine keep their own, plus a device-level last-used copy for the sign-in page. Every access is
 * wrapped — storage can be absent, full or throw (a private window) — and the UI falls back down the
 * list.
 *
 * Scope: the STAFF UI only. Client-facing text (emails, SMS, the upload link) follows the client's
 * recorded language on its own path and is never decided here.
 */

export const UI_LANGUAGES = ['en', 'es'] as const;
export type UiLanguage = (typeof UI_LANGUAGES)[number];

/** The language's own name for itself, which is how a switcher must label it. */
export const LANGUAGE_ENDONYM: Record<UiLanguage, string> = { en: 'English', es: 'Español' };

const KEY_PREFIX = 'ptc-supervision:ui-language:';
/** The last language used on this device, so the sign-in page matches it before anyone is known. */
export const LAST_USED_KEY = `${KEY_PREFIX}last`;

export function choiceStorageKey(userKey: string | null | undefined): string {
  return `${KEY_PREFIX}user:${(userKey || 'anonymous').toLowerCase()}`;
}

/** 'es', 'es-MX', 'ES_us' → 'es'; anything this UI does not speak → null. */
export function normalizeLanguage(code: string | null | undefined): UiLanguage | null {
  const base = (code ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return (UI_LANGUAGES as readonly string[]).includes(base) ? (base as UiLanguage) : null;
}

export interface LanguageInputs {
  /** The person's own choice (server or local cache), if any. */
  choice?: string | null;
  /** `navigator.language` or similar. */
  browser?: string | null;
}

export function resolveLanguage({ choice, browser }: LanguageInputs): UiLanguage {
  return normalizeLanguage(choice) ?? normalizeLanguage(browser) ?? 'en';
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readStored(key: string): UiLanguage | null {
  try {
    return normalizeLanguage(storage()?.getItem(key));
  } catch {
    return null;
  }
}

export function writeStored(key: string, lang: UiLanguage): void {
  try {
    storage()?.setItem(key, lang);
  } catch {
    // Storage full or blocked: the choice still applies for this session.
  }
}

/** The AAD object id MSAL reports, without the "objectId.tenantId" suffix some accounts carry. */
export function userKeyFromAccount(account: { localAccountId?: string | null } | null | undefined): string | null {
  const id = account?.localAccountId?.split('.')[0]?.trim();
  return id ? id.toLowerCase() : null;
}
